import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { trigger, transition, style, animate } from '@angular/animations';
import { MonitoringService } from 'src/app/services/api/monitoring.service';
import { TenantsService } from 'src/app/services/api/tenants.service';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-environment-monitoring',
  templateUrl: './environment-monitoring.component.html',
  styleUrls: ['./environment-monitoring.component.scss'],
  animations: [
    trigger('rotate', [
      transition('void=>*', [style({ transform: 'rotate(0)' })]),
      transition('* => *', [
        style({ transform: 'rotate(0)' }),
        animate('650ms ease-in-out', style({ transform: 'rotate(360deg)' }))
      ])
    ]),
    trigger('donutAnimation', [
      transition(':enter', [
        style({ transform: 'scale(0.8)', opacity: 0 }),
        animate('600ms ease-out', style({ transform: 'scale(1)', opacity: 1 }))
      ])
    ])
  ],
  standalone: false
})
export class EnvironmentMonitoringComponent implements OnInit {
  @Input() envId?: string;
  @Input() searchKey?: string;

  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);
  private tenantsService = inject(TenantsService);
  private monitoringService = inject(MonitoringService);

  tenant = this.authService.principal.provider;
  isExternalClient = false;

  isLoading = true;
  nonProdZone: any = null;
  noMonitoringData = false;
  activeStatus = 'HEALTHY';
  selectedItem: any = null;
  donutAnimationState = 0;
  count = 0;

  ngOnInit(): void {
    this.tenantsService.getTenant(this.tenant)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (t: any) => {
          this.isExternalClient = t?.origin?.toLowerCase() === 'external';
          this.loadMonitoring();
        },
        error: () => this.loadMonitoring()
      });
  }

  refreshMonitoring(): void {
    this.isLoading = true;
    this.selectedItem = null;
    this.count = this.count !== 1 ? 1 : 0;
    this.loadMonitoring();
  }

  loadMonitoring(): void {
    if (!this.searchKey) {
      this.noMonitoringData = true;
      this.nonProdZone = null;
      this.selectedItem = null;
      this.isLoading = false;
      return;
    }
    this.monitoringService.getRagStatus(this.tenant)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          if (!data?.managementZones || data.managementZones.length === 0) {
            this.noMonitoringData = true;
            this.nonProdZone = null;
            this.selectedItem = null;
            this.isLoading = false;
            return;
          }
          this.noMonitoringData = false;
          const zones = data.managementZones.map((zone: { managementZone: any; rag_list: any; }) => {
            const summary = this.summarizeZone(zone);
            return {
              name: zone.managementZone,
              services: summary.services,
              healthy: summary.healthy,
              critical: summary.critical,
              impacted: summary.impacted,
              overallStatus: this.getOverallStatus(summary),
              details: zone.rag_list
            };
          });

          const key = (this.searchKey ?? '').toLowerCase();
          if (!this.isExternalClient) {
            this.nonProdZone = zones.find((item: { name: string; }) => item.name.toLowerCase().includes(key));
          } else {
            this.nonProdZone = zones.find((item: { name: string; }) =>
              item.name.split(':')[0].trim().toLowerCase().includes(key));
          }
          if (this.nonProdZone) {
            this.nonProdZone.donutStyle = this.getDonutStyle(this.nonProdZone);
            this.selectFirstItem();
          }
          this.donutAnimationState++;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.noMonitoringData = true;
        }
      });
  }

  selectFirstItem(): void {
    const filtered = this.nonProdZone?.details.filter((item: { rag: string; }) => this.ragToStatus(item.rag) === this.activeStatus);
    if (filtered?.length) {
      this.selectItem(filtered[0]);
    }
  }

  summarizeZone(zone: any) {
    const summary = { services: zone.rag_list.length, healthy: 0, critical: 0, impacted: 0 };
    zone.rag_list.forEach((item: { rag: string; }) => {
      if (item.rag === 'GREEN') { summary.healthy++; }
      else if (item.rag === 'AMBER') { summary.impacted++; }
      else if (item.rag === 'RED') { summary.critical++; }
    });
    return summary;
  }

  getOverallStatus(summary: any): string {
    if (summary.impacted > 0) { return 'IMPACTED'; }
    if (summary.critical > 0) { return 'CRITICAL'; }
    return 'HEALTHY';
  }

  ragToStatus(rag: string): string {
    switch (rag) {
      case 'GREEN': return 'HEALTHY';
      case 'AMBER': return 'IMPACTED';
      case 'RED': return 'CRITICAL';
      default: return '';
    }
  }

  getFilteredItems(zone: any): any[] {
    return zone.details.filter((item: { rag: string; }) => this.ragToStatus(item.rag) === this.activeStatus);
  }

  onStatusChange(status: string): void {
    this.activeStatus = status;
    this.selectedItem = null;
    this.selectFirstItem();
  }

  selectItem(item: any): void {
    this.selectedItem = item;
  }

  getDonutStyle(zone: any) {
    const total = zone.services ?? 0;
    if (total === 0) {
      return { background: 'conic-gradient(#d3d3d3 0deg 360deg)' };
    }
    const hEnd = ((zone.healthy ?? 0) / total) * 360;
    const iEnd = hEnd + ((zone.impacted ?? 0) / total) * 360;
    const cEnd = iEnd + ((zone.critical ?? 0) / total) * 360;
    return {
      background: `conic-gradient(
        #188038 0deg ${hEnd}deg,
        #EF7839 ${hEnd}deg ${iEnd}deg,
        #B53B20 ${iEnd}deg ${cEnd}deg
      )`
    };
  }
}